// src/api.ts
import { User } from "oidc-client-ts";
import { Equipment, FAQ, MemberPermissionDTO } from "./types";

const BASE =  process.env.REACT_APP_API_URL || "http://localhost:5023";
const AUTHORITY = process.env.REACT_APP_AUTHORITY || "https://whodis.unify.fun";
const CLIENTID = process.env.REACT_APP_CLIENT_ID || "tickets-dev";

function getUser() {
  const oidcStorage = localStorage.getItem(
    `oidc.user:${AUTHORITY}:${CLIENTID}`
  );
  if (!oidcStorage) {
    return null;
  }

  return User.fromStorageString(oidcStorage);
}

export async function fetchFaults() {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/faults`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function fetchFault(id: number) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/faults/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function createUser(
  site: string,
  dto: { sentence: string; name: string }
): Promise<User> {
  const _user = getUser();
  const token = _user?.access_token;

  const res = await fetch(`${BASE}/members/site/${site}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(dto),
  });
  if (res.status === 409) {
    throw new Error("Member already exists for this site.");
  }
  if (!res.ok) {
    throw new Error("Failed to create member");
  }
  return res.json();
}

export async function createFault(description: string) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/faults`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ description: description }),
    })
  ).json();
}

export async function updateFault(id: number, description: string) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/faults`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ id: id, description: description }),
    })
  ).json();
}

export async function submitTicket(
  site: string,
  userName: string,
  submittedByUser: string,
  faultIds: number[],
  location: string
) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/tickets`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        site,
        userName,
        location,
        submittedByUser,
        faultIds,
      }),
    })
  ).json();
}

export async function fetchTickets(site: string) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/tickets/site/${site}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function fetchUserTickets(user: string, site: string) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/tickets/site/${site}/user/${user}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function fetchTicketById(id: number) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/tickets/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function fetchMembers(site: string) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/members/site/${site}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function fetchMemberBySentence(sentence: string) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/members/sentence/${sentence}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function deleteUser(id: number) {
  const _user = getUser();
  const token = _user?.access_token;

  return await fetch(`${BASE}/members/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function closeTicket(id: number) {
  const _user = getUser();
  const token = _user?.access_token;

  return (
    await fetch(`${BASE}/tickets/${id}/close`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function addNoteToTicket(ticketId: number, created: string, text: string) {
  const _user = getUser();
  const token = _user?.access_token;

  if (_user === null) {
    return;
  }

  const createdByUser = _user.profile.sub;

  return (
    await fetch(`${BASE}/tickets/${ticketId}/addnote`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text, created, createdByUser }),
    })
  ).json();
}

export async function updateTicketStatus(id: number, newStatus: string) {
  const _user = getUser();
  const token = _user?.access_token;

  let status = 0;

  switch (newStatus) {
    case "Requested":
      status = 0;
      break;
    case "Closed":
      status = 1;
      break;
    case "InProgress":
      status = 2;
      break;
    case "EscalatedToUnify":
      status = 3;
      break;
    default:
      throw new Error("Invalid Ticket Status");
  }

  return (
    await fetch(`${BASE}/tickets/${id}/update/${status}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function fetchFAQ(site:string) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/${site}/FAQ`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function fetchFAQById(site:string, id:number) {
    const _user = getUser();
  if (_user === null) {
    return;
  }
  
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/${site}/FAQ/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  ).json();
}

export async function createFAQ(site:string, newFAQDto:FAQ) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/${site}/FAQ`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({...newFAQDto}),
    })
  ).json();
}

export async function updateFAQ(site:string, faqDto:FAQ) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
  await fetch(`${BASE}/${site}/FAQ`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({...faqDto}),
  })
  ).json();
}

export async function deleteFAQ(site:string, id:number) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return await fetch(`${BASE}/${site}/FAQ/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getPreviousNotes() {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
      await fetch(`${BASE}/tickets/notes/previous`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }
    )
  ).json();
}

export async function updateRating(id:number, rating:"Positive" | "Negative") {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (await fetch(`${BASE}/tickets/${id}/rate/${rating}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    }
  })).json()
}

export async function fetchEquipment(site: string) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;
  return (
    await fetch(`${BASE}/${site}/Equipment`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  ).json();
}

export async function createEquipment(site: string, description: string) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  await fetch(`${BASE}/${site}/Equipment/new`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ id: 0, description: description })
  })
};

export async function updateEquipment(site: string, dto: Equipment) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/${site}/Equipment/update`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({...dto})
    })
  )
}

export async function fetchDistributedEquipment(site: string) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/${site}/Equipment/distributed`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  ).json();
}

export async function addEquipmentToTicket(ticketId:number, equipment:string[]) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/tickets/${ticketId}/equipment`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(equipment)
    })
  ).json()
}

export async function deleteEquipment(site:string, id:number) {
    const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return await fetch(`${BASE}/${site}/Equipment/${id}`,  {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
  })
}

export async function updateMemberPermissions(id:number, dto:MemberPermissionDTO) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/Members/${id}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(dto)
    })
  ).json()
}

export async function canUserCreateTicket(site:string, sentence:string) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/Tickets/${site}/${sentence}/canCreate`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  ).json()
}

export async function canUserCreateTicketForOthers(site:string, sentence:string) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/Tickets/${site}/${sentence}/canCreate/forOthers`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  ).json()
}

export async function fetchCanRespondToTickets(site:string, sentence:string) {
  const _user = getUser();
  if (_user === null) {
    return;
  }
  const token = _user.access_token;

  return (
    await fetch(`${BASE}/Tickets/${site}/${sentence}/canRespond`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  ).json()
}
